import { CircularProgress, DialogActions, makeStyles } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import foundersAPI, { FounderId } from '../../api/founders';
import Button from './button';
import ModalV2 from './modal-v2';
import SnackMessage from './snack-message';
import Text from './text';

const useStyles = makeStyles({
  actions: {
    padding: '32px 0 0',
  },
});

interface FounderInviteModalProps {
  open: boolean;
  founderId: FounderId | null;
  onClose: () => void;
}

function FounderInviteModal({
  open,
  founderId,
  onClose,
}: FounderInviteModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState(false);

  const handleSendInvite = useCallback(async () => {
    try {
      if (!founderId) {
        return;
      }

      setIsLoading(true);
      await foundersAPI.sendInvite([founderId]);
      onClose();
      enqueueSnackbar('The invitation was successfully sent!', {
        variant: 'success',
      });
    } catch (error: any) {
      const messageError = error.response?.data?.message;
      enqueueSnackbar(
        'An error occurred when sending the invitation. Please try again.',
        {
          content: (key, message) =>
            SnackMessage({
              key,
              message,
              variant: 'error',
              additionalMessage: messageError,
            }),
          variant: 'error',
        },
      );
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar, founderId, onClose]);

  return (
    <ModalV2
      width='480px'
      open={open}
      onClose={onClose}
      titleProps={{ title: 'Give Founder Access to Traction5' }}>
      <Text variant='normal'>
        An invite to join Traction5 will be sent to this Founder. They will be
        able to create an account, login, and review materials on their ventures
        and session. If they already have an account, the invite will be
        re-sent.
      </Text>
      <DialogActions className={classes.actions}>
        <Button
          variant='outlined'
          onClick={onClose}
          data-testid='button-cancel'>
          Cancel
        </Button>
        <Button
          onClick={handleSendInvite}
          disabled={isLoading}
          data-testid='button-success'>
          {isLoading ? (
            <CircularProgress size={24} color='inherit' />
          ) : (
            'Send Invite'
          )}
        </Button>
      </DialogActions>
    </ModalV2>
  );
}

export default FounderInviteModal;
